import axios from 'axios';
import { appVersion, appPlatform } from './nativeApp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const HMAC_SECRET = process.env.REACT_APP_HMAC_SECRET || '';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// HMAC-SHA256 signing function (using Web Crypto API with fallback)
async function signRequest(method, path) {
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const message = `${timestamp}:${method}:${path}`;

  if (typeof crypto !== 'undefined' && crypto.subtle) {
    // Modern browsers, iOS WKWebView, Android WebView
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
      'raw', encoder.encode(HMAC_SECRET), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
    );
    const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(message));
    const hex = Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
    return { timestamp, signature: hex };
  }

  // No Web Crypto (non-secure context): do NOT emit a fake, forgeable signature.
  return null;
}

// Route params placed into API paths. react-router hands params over already decoded, so a URL
// like /patrona/..%2F..%2Fadmin%2Fusers arrives as "../../admin/users" and, pasted into a path
// unencoded, would send the user's token to /api/admin/users. encodeURIComponent keeps a value
// inside its own segment; "." and ".." are refused outright because the URL parser resolves dot
// segments even when they are percent-encoded. A refusal is shaped like a 404 so the page shows
// its normal "no encontrado".
const notFound = () => Promise.reject(Object.assign(new Error('Not found'), { response: { status: 404 } }));
const pathSegment = (v) => {
  const s = String(v ?? '');
  return s && s !== '.' && s !== '..' ? encodeURIComponent(s) : null;
};
// Patrona slugs are nested ("concursos/tortilla"); the backend accepts only [a-z0-9-/].
const PATRONA_SLUG = /^[a-z0-9-]+(\/[a-z0-9-]+)*$/;

// Add auth token + HMAC signature to requests
api.interceptors.request.use(async (config) => {
  // Blocked storage (private mode, site data off) throws on access; that must not fail every request.
  let token = null;
  try { token = localStorage.getItem('torre_token'); } catch { /* storage unavailable */ }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Say which app shell this came from, when it came from one.
  //
  // The web and the shell ship on completely different clocks — minutes versus a store review
  // and then however long people take to update — so at any moment the installed base is spread
  // across several shell versions and there was no way to see that spread. Without it, deciding
  // whether an old-shell workaround can be retired is guesswork. The header costs nothing (it is
  // not part of the HMAC message, which signs timestamp:method:path only, so it cannot affect
  // signing) and the backend records it on one endpoint rather than on every request.
  const shellVersion = appVersion();
  if (shellVersion) {
    config.headers['X-App-Version'] = shellVersion;
    config.headers['X-App-Platform'] = appPlatform() || 'unknown';
  }

  // Add HMAC signature
  if (HMAC_SECRET) {
    const urlPath = '/api' + (config.url.startsWith('/') ? config.url : '/' + config.url).split('?')[0];
    const signed = await signRequest(config.method.toUpperCase(), urlPath);
    if (signed) {
      config.headers['X-App-Timestamp'] = signed.timestamp;
      config.headers['X-App-Signature'] = signed.signature;
    }
  }

  return config;
});

// Auth. Sign-in is by emailed code, Apple or Google; the password and verification-link routes
// were retired on 2026-09-26 and answer 410.
export const authAPI = {
  requestMagicCode: (email) => api.post('/auth/magic-code', { email }),
  verifyMagicCode: (data) => api.post('/auth/verify-code', data),
  appleSignIn: (data) => api.post('/auth/apple', data),
  googleSignIn: (data) => api.post('/auth/google', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  setEmailSubscription: (subscribed) => api.put('/auth/email-subscription', { subscribed }),
  // Perfil's «Aparecer en la clasificación de vecinos».
  setRankingVisibility: (visible) => api.put('/auth/ranking-visibility', { visible }),
  logoutAll: () => api.post('/auth/logout-all'),
  exportData: () => api.get('/auth/export-data'),
  // Deleting needs a code sent to the account's address, not only a session.
  requestDeleteCode: () => api.post('/auth/delete-account/code'),
  deleteAccount: (code) => api.delete('/auth/delete-account', { data: { code } }),
};

// Weather
// Consulta de parcela (routes/parcela.py). Every call is a read of our own stored data except
// inmuebles(), which asks the Catastro once a month per parcel (server-side cache).
export const parcelaAPI = {
  estado: () => api.get('/parcela/estado'),
  buscar: (q, config) => api.get('/parcela/buscar', { params: { q }, ...config }),
  informe: (ref, config) => api.get(`/parcela/${encodeURIComponent(ref)}`, config),
  inmuebles: (ref, config) => api.get(`/parcela/${encodeURIComponent(ref)}/inmuebles`, { timeout: 20000, ...config }),
  mapa: (bbox, config) => api.get('/parcela/mapa', { params: { bbox }, ...config }),
  capa: (nombre, bbox, config) => api.get(`/parcela/capas/${encodeURIComponent(nombre)}`, { params: { bbox }, ...config }),
  en: (lon, lat) => api.get('/parcela/en', { params: { lon, lat } }),
  entorno3d: (ref, config) => api.get(`/parcela/${encodeURIComponent(ref)}/entorno3d`, config),
};

// The report PDF (a link, not an axios call: the browser or the phone opens it).
export const parcelaPdfUrl = (ref) => `${API_BASE}/parcela/${encodeURIComponent(ref)}/informe.pdf`;
// The extract of the PGOU's 1996 zoning sheet round the parcel (an <img>; backend plano1996.py).
export const parcelaPlanoUrl = (ref) => `${API_BASE}/parcela/${encodeURIComponent(ref)}/plano1996.png`;
// The 3D view's terrain tiles (MapLibre fills in {z}/{x}/{y}; backend terrain3d.py). Versioned: kept a year.
export const parcelaTerrenoTiles = (version) => `${API_BASE}/parcela/terreno/v/${encodeURIComponent(version)}/{z}/{x}/{y}.png`;
export const parcelaArboladoTiles = (version) => `${API_BASE}/parcela/arbolado/v/${encodeURIComponent(version)}/{z}/{x}/{y}.png`;

export const urbanismoAPI = {
  registro: (config) => api.get('/urbanismo', config),
};

export const weatherAPI = {
  get: () => api.get('/weather'),
  forecast: () => api.get('/weather/forecast'),
  enhanced: () => api.get('/tiempo'),
  alerts: () => api.get('/tiempo/alerts'),
  maritime: () => api.get('/tiempo/maritime'),
  observation: () => api.get('/weather/observation'),
};

// Search
export const searchAPI = {
  // limit: 10 by default; the server caps it at 20 (services/search.py), which /buscar asks for.
  query: (q, limit) => api.get('/search', { params: limit ? { q, limit } : { q } }),
};

// Incidencias
export const incidenciasAPI = {
  list: (params) => api.get('/incidencias', { params }),
  get: (id) => api.get(`/incidencias/${id}`),
  // Sending a report waits for the server's AI checks (at most 10 s at create, 12 s for validate,
  // routes/incidencias.py) plus the photo's upload on a phone signal: 30 s, not the default 15 s,
  // after which the app said «Error» while the server went on to save the report.
  create: (data) => api.post('/incidencias', data, { timeout: 30000 }),
  classify: (titulo, descripcion) => api.post('/incidencias/classify', { titulo, descripcion }),
  validate: (data) => api.post('/incidencias/validate', data, { timeout: 30000 }),
  votar: (id) => api.post(`/incidencias/${id}/votar`),
  comment: (id, data) => api.post(`/incidencias/${id}/comment`, data),
  editComment: (incId, commentId, data) => api.put(`/incidencias/${incId}/comments/${commentId}`, data),
  deleteComment: (incId, commentId) => api.delete(`/incidencias/${incId}/comments/${commentId}`),
  updateStatus: (id, estado) => api.put(`/incidencias/${id}/status`, { estado }),
  addPhoto: (id, foto_base64) => api.post(`/incidencias/${id}/photo`, { foto_base64 }),
  locations: () => api.get('/incidencias/locations'),
  updateLocation: (id, lat, lon, barrio, direccion) =>
    api.patch(`/incidencias/${id}/location`, { lat, lon, barrio, direccion }),
  // The author's edit (title, description): checked by the AI before it replaces the published text.
  edit: (id, data) => api.patch(`/incidencias/${id}`, data, { timeout: 30000 }),
};

// Geocoding
export const geocodeAPI = {
  reverse: (lat, lon) => api.get('/geocode/reverse', { params: { lat, lon } }),
  search: (q) => api.get('/geocode/search', { params: { q } }),
};

// Bulletins
export const bulletinsAPI = {
  list: () => api.get('/bulletins'),
  highlights: () => api.get('/bulletins/highlights'),
};

// Payment windows of Torrelavega's taxes, read from the BOC's padrón notices (Boletines → Plazos, Inicio).
export const plazosAPI = {
  list: () => api.get('/plazos'),
};

// Map
export const mapAPI = {
  // todas=1 (/torrebus only): also the stops Torrebus serves without a usable position (lat/lon
  // null, `sin_posicion`), listed without a pin. Mapa Vivo draws stops, so it asks without it.
  busStops: (params) => api.get('/map/bus-stops', params ? { params } : undefined),
  parking: () => api.get('/map/parking'),
  construction: () => api.get('/map/construction'),
  services: () => api.get('/map/services'),
  // meta=1: {stations, prices_date, prices_are_today, _freshness}; without it, the plain list older bundles read.
  fuelStations: () => api.get('/map/fuel-stations', { params: { meta: 1 } }),
  evStations: () => api.get('/map/ev-stations', { params: { meta: 1 } }),
};

// Torrebus
export const torrebusAPI = {
  lines: () => api.get('/torrebus/lines'),
  // params.wait=0: previews do not wait for a stop's first reading (see map_data.py).
  // Stop codes come from upstream (data.torrebus.es via the stop cache), so they go through
  // pathSegment like any other value placed in a path. `config` carries e.g. an AbortSignal.
  arrivals: (stopCode, params, config) => {
    const seg = pathSegment(stopCode);
    return seg ? api.get(`/torrebus/arrivals/${seg}`, { ...config, ...(params ? { params } : {}) }) : notFound();
  },
  arrivalsLive: (stopCode, config) => {
    const seg = pathSegment(stopCode);
    return seg ? api.get(`/torrebus/arrivals/${seg}/live`, config) : notFound();
  },
  nearest: (lat, lon) => api.get(`/torrebus/nearest?lat=${lat}&lon=${lon}`),
};

// Concerts
export const concertsAPI = {
  list: () => api.get('/concerts'),
};

// Sports
export const deportesAPI = {
  fixtures: () => api.get('/deportes'),
  liveScores: () => api.get('/live-scores'),
};

// Parking / Aparcamiento
export const parkingAPI = {
  zones: () => api.get('/parking/zones'),
  estimation: () => api.get('/parking/estimation'),
  live: () => api.get('/parking/live'),
};

// Vecinos
export const vecinosAPI = {
  list: (params) => api.get('/vecinos', { params }),
  detail: (id) => api.get(`/vecinos/${id}`),
  stats: () => api.get('/vecinos/stats'),
  history: () => api.get('/vecinos/historial'),
  // As incidenciasAPI: the server's AI check takes up to 10 s at create and 12 s at validate
  // (routes/vecinos.py); at the default 15 s the app said «Error» while the request was saved.
  create: (data) => api.post('/vecinos', data, { timeout: 30000 }),
  validate: (data) => api.post('/vecinos/validate', data, { timeout: 30000 }),
  classify: (titulo, descripcion) => api.post('/vecinos/classify', { titulo, descripcion }),
  // The author's edit while open and nobody has offered (routes/vecinos.edit_help_request).
  edit: (id, data) => api.patch(`/vecinos/${id}`, data, { timeout: 30000 }),
  offer: (id) => api.post(`/vecinos/${id}/offer`),
  acceptCandidate: (id) => api.post(`/vecinos/${id}/accept-candidate`),
  declineCandidate: (id) => api.post(`/vecinos/${id}/decline-candidate`),
  cancelHelp: (id) => api.post(`/vecinos/${id}/cancel-help`),
  withdraw: (id) => api.post(`/vecinos/${id}/withdraw`),
  accept: (id) => api.post(`/vecinos/${id}/accept`),
  complete: (id) => api.post(`/vecinos/${id}/complete`),
  close: (id) => api.post(`/vecinos/${id}/close`),
  getMessages: (requestId) => api.get(`/vecinos/${requestId}/messages`),
  sendMessage: (requestId, texto) => api.post(`/vecinos/${requestId}/messages`, { texto }),
  myThreads: () => api.get('/vecinos/my-threads'),
};

// Air Quality
export const airQualityAPI = {
  get: () => api.get('/air-quality'),
};

// Comercio
export const comercioAPI = {
  alerts: () => api.get('/comercio/alerts'),
  directory: (params = {}) => api.get('/comercio/directory', { params }),
  markers: () => api.get('/comercio/markers'),
  shop: (id) => api.get(`/comercio/shop/${encodeURIComponent(id)}`),
  rutaDelDia: () => api.get('/comercio/ruta-del-dia'),
  // Takes a shop, or a raw photo ref. The directory endpoint no longer ships photo_ref — it was
  // 273KB of the 681KB response, a ~340-character Google token per shop that this function did
  // nothing with but hand straight back — so it sends has_photo and the server resolves the ref
  // from the shop id. Endpoints that carry few shops (ruta del día, the detail page) still send
  // photo_ref, and a shop that has one still uses it, so both shapes work and nothing has to be
  // deployed in a particular order.
  photoUrl: (shopOrRef) => {
    if (!shopOrRef) return null;
    if (typeof shopOrRef === 'string') return `/api/comercio/photo?name=${encodeURIComponent(shopOrRef)}`;
    if (shopOrRef.photo_ref) return `/api/comercio/photo?name=${encodeURIComponent(shopOrRef.photo_ref)}`;
    if (shopOrRef.has_photo && shopOrRef.id) return `/api/comercio/photo?shop=${encodeURIComponent(shopOrRef.id)}`;
    return null;
  },
};

// Agenda
export const agendaAPI = {
  list: () => api.get('/agenda'),
  detail: (id) => api.get(`/agenda/${encodeURIComponent(id)}`),
  venues: () => api.get('/agenda/venues'),
  // The weekly markets and livestock fairs (the «Cada semana» block), each with its next date.
  semanal: () => api.get('/agenda/semanal'),
};

// Theatre
export const theatreAPI = {
  list: () => api.get('/theatre'),
};

// Torrebici
export const torrebiciAPI = {
  stations: () => api.get('/torrebici/stations'),
  nearest: (lat, lon) => api.get(`/torrebici/nearest?lat=${lat}&lon=${lon}`),
  availability: () => api.get('/torrebici/availability'),
  report: (data) => api.post('/torrebici/report', data),
};

// Renfe Cercanías
export const renfeAPI = {
  stations: () => api.get('/renfe/stations'),
  // Station codes come from Renfe's GTFS and from the ?estacion= link parameter.
  departures: (code) => {
    const seg = pathSegment(code);
    return seg ? api.get(`/renfe/departures/${seg}`) : notFound();
  },
  live: () => api.get('/renfe/live'),
  realtime: () => api.get('/renfe/realtime'),
};

export const busSantanderAPI = {
  schedule: () => api.get('/bus-santander/schedule'),
};

// Servicios
export const serviciosAPI = {
  list: () => api.get('/servicios'),
};

// Residuos
export const residuosAPI = {
  contenedores: () => api.get('/residuos/contenedores'),
  nearest: (lat, lon) => api.get(`/residuos/nearest?lat=${lat}&lon=${lon}`),
  info: () => api.get('/residuos/info'),
};

// Traffic (DGT)
export const trafficAPI = {
  summary: () => api.get('/trafico/resumen'),
  status: () => api.get('/trafico/estado'),
  incidents: () => api.get('/trafico/incidencias'),
  cameras: () => api.get('/trafico/camaras'),
};

export const beachAPI = {
  list: () => api.get('/playas'),
  waterQuality: () => api.get('/playas/water-quality'),
  surf: () => api.get('/playas/surf'),
};


export const mountainAPI = {
  list: () => api.get('/montanas'),
};

export const pollenAPI = {
  current: () => api.get('/polen'),
};

export const dashboardAPI = {
  getAll: () => api.get('/dashboard'),
};

export const radarAPI = {
  get: () => api.get('/radar'),
};




// City Assistant
export const assistantAPI = {
  // session_token is issued and signed by the server; new_session starts a fresh conversation (same device quota)
  chat: (message, session_token, new_session = false) =>
    api.post('/assistant/chat', { message, session_token: session_token || '', new_session }, { timeout: 30000 }), // > the server's 25 s deadline (engine.QUESTION_DEADLINE_S), so the user gets its reply, not «Error de conexión»
  // The question length limit admins set (lib/torreLimits.js).
  config: () => api.get('/assistant/config'),
};

export const torreAdminAPI = {
  settings: () => api.get('/admin/torre/settings'),
  updateSettings: (patch) => api.patch('/admin/torre/settings', patch),
  usage: (days = 30) => api.get('/admin/torre/usage', { params: { days } }),
  evals: () => api.get('/admin/torre/evals'),
  runEvals: () => api.post('/admin/torre/evals/run'),
};

// Stats & Karma
export const statsAPI = {
  get: () => api.get('/stats'),
  leaderboard: () => api.get('/karma/leaderboard'),
};

export const karmaAPI = {
  leaderboard: () => api.get('/karma/leaderboard'),
  // Where you stand and what the next step costs. Separate from /dashboard because that response
  // is one cached document shared by every caller and cannot hold anything personal.
  me: () => api.get('/karma/me'),
  history: () => api.get('/karma/history'),
  dailyLogin: () => api.post('/karma/daily-login'),
  levels: () => api.get('/karma/levels'),
};

export const transparenciaAPI = {
  dashboard: () => api.get('/transparencia'),
};

// Notifications
export const notificationsAPI = {
  categories: () => api.get('/notifications/categories'),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
  history: (limit = 20) => api.get('/notifications/history', { params: { limit } }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/read/${id}`),
  markAllRead: () => api.put('/notifications/read-all'),
  // Town-wide pushes of the last 7 days, for everyone (the bell).
  broadcasts: (limit = 20) => api.get('/notifications/broadcasts', { params: { limit } }),
};

// River Levels
export const riverLevelsAPI = {
  get: () => api.get('/river-levels'),
  alerts: () => api.get('/river-alerts'),
  embalses: () => api.get('/embalses'),
};

// Farmacia de Guardia
export const farmaciaGuardiaAPI = {
  get: () => api.get('/farmacia-guardia'),
};

// All Pharmacies (OSM + Google merged)
export const farmaciasAPI = {
  list: () => api.get('/farmacias'),
};

// Estadisticas
export const estadisticasAPI = {
  get: () => api.get('/estadisticas'),
  aiSummary: () => api.get('/estadisticas/ai-summary'),
  combined: () => api.get('/radiografia/combined'),
};

// Tramites
export const tramitesAPI = {
  list: () => api.get('/tramites'),
  detail: (id) => {
    const seg = pathSegment(id);
    return seg ? api.get(`/tramites/${seg}`) : notFound();
  },
  ask: (data) => api.post('/tramites/ask', data),
  scanRecibo: (data) => api.post('/tramites/scan-recibo', data, { timeout: 30000 }), // server: 13 s (SCAN_AI_TIMEOUT_S)
};

// Admin Dashboard
export const adminAPI = {
  urbanismoNovedades: () => api.get('/admin/urbanismo/novedades'),
  urbanismoActualizar: () => api.post('/admin/urbanismo/actualizar'),
  parcelaImportar: () => api.post('/admin/parcela/importar'),
  cedula: (ref, config) => api.get(`/admin/parcela/${encodeURIComponent(ref)}/cedula`, config),
  health: () => api.get('/admin/health'),
  users: () => api.get('/admin/users'),
  content: () => api.get('/admin/content'),
  aiUsage: () => api.get('/admin/ai-usage'),
  externalApis: () => api.get('/admin/external-apis'),
  notifications: () => api.get('/admin/notifications'),
  errorLog: () => api.get('/admin/error-log'),
  refreshCache: () => api.post('/admin/refresh-cache'),
  refreshCategory: (cat) => api.post(`/admin/refresh-cache/${cat}`),
  refreshSports: () => api.post('/admin/refresh-sports'),
  refreshBulletins: () => api.post('/admin/refresh-bulletins'),
  refreshPatrona: () => api.post('/admin/refresh-patrona'),
  refreshTiles: () => api.post('/admin/refresh-tiles'),
  cacheHealth: () => api.get('/admin/cache-health'),
  // Per scheduler job last OK / last error / failures in a row, and each cache entry's freshness
  sourcesHealth: () => api.get('/admin/sources/health'),
  importDgtLabels: () => api.post('/admin/import-dgt-labels'),
  // Moderation
  moderationQueue: () => api.get('/admin/moderate/queue'),
  moderationStats: () => api.get('/admin/moderate/stats'),
  moderationLog: (limit) => api.get(`/admin/moderate/log${limit ? `?limit=${limit}` : ''}`),
  moderationComments: (status) => api.get(`/admin/moderate/comments?status=${status}`),
  moderateComment: (incId, commentId, action, reason) => api.patch(`/admin/moderate/comment/${incId}/${commentId}`, { action, reason: reason || '' }),
  rescreenComment: (incId, commentId) => api.post(`/admin/moderate/comment/${incId}/${commentId}/rescreen`),
  moderateContent: (type, id, action, reason) => api.patch(`/admin/moderate/${type}/${id}`, { action, reason }),
  deleteContent: (type, id) => api.delete(`/admin/moderate/${type}/${id}`),
  changeEstado: (id, estado, reason) => api.patch(`/admin/moderate/incidencia/${id}/estado`, { estado, reason }),
  // Private messages the AI never answered for (approve delivers, reject tells the sender).
  moderationMessages: () => api.get('/admin/moderate/messages'),
  decideMessage: (id, action) => api.post(`/admin/moderate/messages/${id}/decision`, { action }),
  getFeedback: () => api.get('/admin/feedback'),
  updateFeedbackStatus: (id, status) => api.patch(`/admin/feedback/${id}`, { status }),
  replyToFeedback: (id, message) => api.post(`/admin/feedback/${id}/reply`, { message }),
  getFixtureOverrides: () => api.get('/admin/config/fixture-overrides'),
  createFixtureOverride: (data) => api.post('/admin/config/fixture-override', data),
  deleteFixtureOverride: (id) => api.delete(`/admin/config/fixture-override/${id}`),
  getDirectorio: () => api.get('/admin/config/directorio'),
  saveDirectorio: (id, data, crear = false) => api.put(`/admin/config/directorio/${encodeURIComponent(id)}`, data, { params: crear ? { crear: true } : {} }),
  deleteDirectorio: (id) => api.delete(`/admin/config/directorio/${encodeURIComponent(id)}`),
  warnUser: (userId, reason) => api.patch(`/admin/moderate/users/${userId}/warn`, { reason }),
  banUser: (userId, reason) => api.patch(`/admin/moderate/users/${userId}/ban`, { reason }),
  unbanUser: (userId) => api.patch(`/admin/moderate/users/${userId}/unban`),
  userHistory: (userId) => api.get(`/admin/moderate/users/${userId}/history`),
  // Events
  listEvents: () => api.get('/admin/events'),
  createEvent: (data) => api.post('/admin/events', data),
  deleteEvent: (id) => api.delete(`/admin/events/${id}`),
  notifyEvent: (id) => api.post(`/admin/events/${id}/notify`),
  testNotifyEvent: (id) => api.post(`/admin/events/${id}/notify?test=true`),
  // Obras y cortes (backend/routes/admin.py): no delete, only finalizar/archivar
  listObras: () => api.get('/admin/obras'),
  createObra: (data) => api.post('/admin/obras', data),
  updateObra: (id, data) => api.put(`/admin/obras/${encodeURIComponent(id)}`, data),
  finalizarObra: (id) => api.post(`/admin/obras/${encodeURIComponent(id)}/finalizar`),
  archivarObra: (id) => api.post(`/admin/obras/${encodeURIComponent(id)}/archivar`),
  // Suggestions from council news (backend/routes/obras_sugerencias.py): approve creates the obra
  listSugerencias: (estado = 'abiertas') => api.get('/admin/obras-sugerencias', { params: { estado } }),
  descartarSugerencia: (id, motivo = '') => api.post(`/admin/obras-sugerencias/${encodeURIComponent(id)}/descartar`, { motivo }),
  aprobarSugerencia: (id, data) => api.post(`/admin/obras-sugerencias/${encodeURIComponent(id)}/aprobar`, data),
  // Config
  featureFlags: () => api.get('/admin/config/flags'),
  toggleFlag: (key, enabled, note) => api.patch(`/admin/config/flags/${key}`, { enabled, note }),
  getAnnouncement: () => api.get('/admin/config/announcement'),
  setAnnouncement: (data) => api.post('/admin/config/announcement', data),
  clearAnnouncement: () => api.delete('/admin/config/announcement'),
  listUsers: (params = {}) => api.get('/admin/config/users', { params: { q: params.q || '', sort: params.sort || 'recent', limit: params.limit || 50, skip: params.skip || 0, filter: params.filter || '' } }),
  // Configuración → Usuarios: { hide_from_ranking } (and { test_account }), audited on the server.
  setUserRankingFlags: (userId, flags) => api.patch(`/admin/config/users/${encodeURIComponent(userId)}/ranking`, flags),
  schedulerHealth: () => api.get('/admin/config/scheduler-health'),
  errorLog: (lines) => api.get(`/admin/config/error-log?lines=${lines || 50}`),
  notificationStats: () => api.get('/admin/config/notification-stats'),
  getZbeSchedule: () => api.get('/admin/config/zbe-schedule'),
  updateZbeSchedule: (data) => api.post('/admin/config/zbe-schedule', data),
  updateFestivosLocal: (year, festivos) => api.post(`/admin/config/festivos-local/${year}`, { festivos }),
  // Scheduled notifications
  listScheduledNotifs: () => api.get('/admin/config/scheduled-notifications'),
  scheduleNotif: (data) => api.post('/admin/config/scheduled-notifications', data),
  cancelScheduledNotif: (id) => api.delete(`/admin/config/scheduled-notifications/${id}`),
  // Auto-moderation
  getAutomodRules: () => api.get('/admin/config/automod-rules'),
  updateAutomodRules: (rules) => api.post('/admin/config/automod-rules', rules),
  // App version
  getAppVersion: () => api.get('/admin/config/app-version'),
  setAppVersion: (data) => api.post('/admin/config/app-version', data),
  // Analytics
  contentAnalytics: () => api.get('/admin/config/analytics'),
  backfillFuel: (days = 30) => api.post(`/combustible/backfill?days=${days}&sync=false`),
  backfillElectricity: (days = 30) => api.post(`/precio-luz/backfill?days=${days}`),
  pollContracts: () => api.post('/contratacion/poll'),
  triggerIndexNow: () => api.post('/admin/trigger-indexnow'),
  // Push notification management
  pushTokens: () => api.get('/notifications/admin/tokens'),
  sendPush: (data) => api.post('/notifications/admin/send', data),
  sendToAdmin: (data) => api.post('/notifications/admin/send-to-admin', data),
  validateToken: (token) => api.post('/notifications/admin/validate-token', { token }),
  testPush: () => api.post('/notifications/test-push'),
};

// Favorites
export default api;


// Fuel prices
export const fuelAPI = {
  list: () => api.get('/combustible'),
  cheapest: () => api.get('/combustible/cheapest'),
  history: (days = 30) => api.get(`/combustible/history?days=${days}`),
  alerts: () => api.get('/combustible/alerts'),
};


// ZBE
export const zbeAPI = {
  info: () => api.get('/zbe/info'),
  checkPlate: (matricula) => api.get(`/zbe/check/${encodeURIComponent(matricula)}`),
};

// Festivos
export const festivosAPI = {
  list: (year) => api.get('/festivos', { params: year ? { year } : undefined }),
  isFestivo: (date) => api.get('/festivos/is-festivo', { params: date ? { date } : undefined }),
};


export const patronaAPI = {
  hub: (config) => api.get('/patrona/hub', config),
  event: (slug) => (PATRONA_SLUG.test(slug || '')
    ? api.get(`/patrona/event/${slug.split('/').map(encodeURIComponent).join('/')}`)
    : notFound()),
  penas: (params) => api.get('/patrona/penas', { params }),
};
